/**
 * One-time clean-slate wipe migration — b13c.7 / v1.4.7 build 1.
 *
 * Pre-b13c.7 builds polluted the Nexus Dex with phantom Shadow/Mega writes
 * (single-frame variant flashes) and wrong-region cells (the classifier-region
 * write key). b13c.7 fixes the pipeline, but a fresh install can't un-write the
 * bad entries already persisted on a tester's device. So the first launch on
 * 1.4.7 performs a ONE-TIME wipe of the dex state, then stamps
 * `lastWipedAppVersion = "1.4.7"` so it never runs again.
 *
 * The decision (`shouldWipe`) is pure and version-only so it is unit-testable
 * without touching storage: wipe iff the stored marker is empty/absent OR
 * strictly older than the target version. A marker equal to (or newer than) the
 * target means this device already ran the wipe — skip it.
 *
 * The runner (`runDexWipeMigration`) wires the decision to the real localStorage
 * marker + the dex store reset. It is best-effort and total: any storage error
 * is swallowed so a wipe failure can never brick app launch.
 */

import { resetNexusDexLocal } from "./dexStateStore";
import { recordBulkTombstone, clearBulkTombstone } from "./dexTombstones";

/** localStorage key holding the last app version whose wipe migration ran. */
export const LAST_WIPED_VERSION_KEY = "nexus.lastWipedAppVersion";

/**
 * b19-fix (PR U) — authoritative wipe markers.
 *
 * The original wipe (`runDexWipeMigration`) cleared ONLY device-local
 * localStorage and intentionally skipped the remote D1 clear (see the
 * historical docstring on `runDexWipeMigration`). That left the server
 * `dex_records` table holding stale rows (e.g. phantom shiny Ceruledge /
 * Rhyperior) for species the user no longer owns, which the trade-match
 * candidate cache kept surfacing as bogus shiny swaps.
 *
 * The fix makes the wipe authoritative across BOTH stores without bundling a
 * network call into the launch-critical local migration. Two SEPARATE pieces
 * of state cooperate:
 *
 *   1. `LAST_WIPED_VERSION_KEY` — the existing "local wipe done" version marker
 *      (unchanged). Drives whether the local dex is cleared.
 *   2. `REMOTE_WIPE_PENDING_KEY` — a one-shot "the server still needs wiping"
 *      flag. Set when a local wipe runs (or is back-filled, see below), read on
 *      every launch by the deferred remote-wipe task, and cleared only after a
 *      successful `DELETE /api/dex/state`.
 *   3. `REMOTE_WIPE_AUDIT_KEY` — a monotonic audit version. Existing users who
 *      ALREADY ran the local wipe in a prior build have their version marker
 *      set and their local dex empty, so the normal `shouldWipe` path is a
 *      no-op for them and would never schedule a server wipe. The audit version
 *      is bumped independently so that, on first launch of THIS build, every
 *      such user gets exactly one `REMOTE_WIPE_PENDING_KEY` set (the
 *      "audit-backfill" path) WITHOUT re-running the local wipe.
 *
 * Why both a "done" flag AND an "audit version" exist: the done flag answers
 * "is the local dex already clear?" while the audit version answers "has THIS
 * build's server-wipe obligation been scheduled yet?". They are orthogonal — a
 * user can be done locally but not yet audited for the server wipe — so a
 * single flag cannot express the migration-already-ran backfill.
 */

/** localStorage key holding the one-shot "server still needs wiping" flag. */
export const REMOTE_WIPE_PENDING_KEY = "nexus.dexRemoteWipePending";
/** localStorage key holding the last remote-wipe audit version applied on this device. */
export const REMOTE_WIPE_AUDIT_KEY = "nexus.dexRemoteWipeAuditVersion";
/**
 * Current remote-wipe audit version shipped by this build. Bump this (and only
 * this) to force a one-shot server wipe on devices whose local wipe already
 * ran in an earlier build. PR U ships version 1.
 *
 * Build 234: bumped from 1 to 2 to re-fire the remote-wipe-pending flag,
 * ensuring devices that ran the prior audit also re-issue the
 * DELETE /api/dex/state call. The server dex_records table is already empty
 * after migration 0029 (pre-launch full wipe), so this DELETE is a no-op
 * safety net that keeps client and server reconciled.
 */
export const REMOTE_WIPE_AUDIT_VERSION = 2;
/**
 * The version whose one-time wipe this build ships.
 *
 * b13i / build 203 (launch-critical): bumped from "1.4.7" to "1.4.7.1" to FORCE
 * a re-wipe on devices that already ran the build-202 wipe (their marker reads
 * "1.4.7"). Build 202's `dexLiveFrameAdapter` stamped every parsed cell CAUGHT,
 * regardless of its per-cell `caught` sprite read, so silhouette-only neighbours
 * (e.g. 023 Ekans, 024 Arbok) were persisted as caught. The pipeline fix lands
 * this build, but it can only stop NEW false positives — the bad entries already
 * on testers' devices must be cleared. The extra 4th numeric segment is required
 * because `compareVersions` parses per-segment integers, so a suffix like
 * "1.4.7-b13i" would parse equal to "1.4.7" (parseInt("7-b13i") === 7) and never
 * re-trigger. Testers re-scan with the corrected per-cell pipeline.
 *
 * Build 234 (launch-critical): bumped from "1.4.7.1" to "1.4.7.2" to FORCE a
 * re-wipe on devices whose marker reads "1.4.7.1". Migration 0029 cleared the
 * server dex_records table as part of the pre-launch full wipe, so local dex
 * state must follow to avoid showing stale ticks on first launch of 234.
 */
export const WIPE_TARGET_VERSION = "1.4.7.2";

/**
 * Compare two dotted numeric version strings. Returns <0 if a<b, 0 if equal,
 * >0 if a>b. Missing segments are treated as 0 ("1.4" == "1.4.0"); non-numeric
 * or empty input sorts as the lowest possible version so it always triggers the
 * wipe. Total — never throws.
 */
export function compareVersions(a: string, b: string): number {
  const parse = (v: string): number[] =>
    (v || "")
      .split(".")
      .map((s) => Number.parseInt(s, 10))
      .map((n) => (Number.isFinite(n) ? n : 0));
  const pa = parse(a);
  const pb = parse(b);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) {
    const da = pa[i] ?? 0;
    const db = pb[i] ?? 0;
    if (da !== db) return da - db;
  }
  return 0;
}

/**
 * Decide whether the one-time wipe should run, given the stored marker and the
 * target version. Wipe iff the marker exists AND is strictly older than the
 * target. Pure — no storage access.
 *
 * 2026-08-30 audit: an ABSENT marker no longer wipes. A missing marker means a
 * FRESH INSTALL (or restored device), which has no pre-b13c.7 pollution to
 * clean — yet the old `!storedVersion -> wipe` arm ran the (empty) wipe, set
 * the remote-wipe-pending flag, and ARMED THE BULK TOMBSTONE on every new
 * device, making each first-launch server restore depend on the RETIRED
 * defuser (runDeferredRemoteWipe) winning a microtask race against the first
 * pull. Genuinely old devices still carry a marker and still wipe.
 */
export function shouldWipe(
  storedVersion: string | null | undefined,
  targetVersion: string = WIPE_TARGET_VERSION,
): boolean {
  if (!storedVersion) return false;
  return compareVersions(storedVersion, targetVersion) < 0;
}

export interface DexWipeResult {
  /** True if this launch performed the wipe. */
  wiped: boolean;
  /** The marker value written after the run (always the target version). */
  markerWritten: string;
  /**
   * True if this launch scheduled a server-side wipe (set the pending marker),
   * either via a fresh local wipe or the audit-version backfill.
   */
  remoteWipeScheduled: boolean;
}

/** Read the one-shot remote-wipe-pending flag. Total — never throws. */
export function isRemoteWipePending(): boolean {
  if (typeof localStorage === "undefined") return false;
  try {
    return localStorage.getItem(REMOTE_WIPE_PENDING_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Set the one-shot remote-wipe-pending flag. `reason` is logged so the device
 * trace distinguishes a fresh wipe from the migration-already-ran backfill.
 */
function setRemoteWipePending(reason: "fresh-wipe" | "audit-backfill"): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(REMOTE_WIPE_PENDING_KEY, "1");
    // eslint-disable-next-line no-console
    console.log("[dex-remote-wipe] marker set", { reason });
  } catch {
    /* quota — the local wipe still ran; the audit version stays un-bumped so
       a future launch re-attempts setting the marker */
  }
}

/** Clear the remote-wipe-pending flag after a successful server wipe. */
export function clearRemoteWipePending(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(REMOTE_WIPE_PENDING_KEY);
  } catch {
    /* quota — worst case the deferred task re-runs and the server DELETE is a
       harmless no-op on an already-empty table */
  }
}

/** Read the stored remote-wipe audit version (0 if absent/unparseable). */
function readRemoteWipeAuditVersion(): number {
  if (typeof localStorage === "undefined") return 0;
  try {
    const raw = localStorage.getItem(REMOTE_WIPE_AUDIT_KEY);
    const n = Number.parseInt(raw ?? "", 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

/** Stamp the current audit version so the backfill runs at most once. */
function writeRemoteWipeAuditVersion(version: number): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(REMOTE_WIPE_AUDIT_KEY, String(version));
  } catch {
    /* quota — backfill may re-evaluate next launch; setting the pending marker
       again is idempotent (the server DELETE is safe to repeat) */
  }
}

/**
 * Run the one-time wipe migration on app launch. Reads the stored marker,
 * clears the dex state if `shouldWipe`, then stamps the target version so the
 * wipe never repeats. Best-effort: storage errors are swallowed and reported as
 * a no-op wipe so launch is never blocked.
 *
 * b19-fix (PR U): the wipe is now authoritative across BOTH stores. The local
 * clear still happens here synchronously (it is the launch-critical part), but
 * a server clear is SCHEDULED — not performed — by setting
 * `REMOTE_WIPE_PENDING_KEY`. The actual `DELETE /api/dex/state` is fired by the
 * deferred `runDeferredRemoteWipe` task after launch so a slow/absent network
 * never blocks startup. Two paths set the pending marker:
 *
 *   - fresh-wipe: a brand-new local wipe ran this launch.
 *   - audit-backfill: the local wipe already ran in an earlier build (so
 *     `shouldWipe` is a no-op), but this build's `REMOTE_WIPE_AUDIT_VERSION` is
 *     newer than what the device recorded, so the server wipe is owed exactly
 *     once. This does NOT re-run the local wipe.
 */
export function runDexWipeMigration(
  targetVersion: string = WIPE_TARGET_VERSION,
): DexWipeResult {
  if (typeof localStorage === "undefined") {
    return { wiped: false, markerWritten: targetVersion, remoteWipeScheduled: false };
  }
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(LAST_WIPED_VERSION_KEY);
  } catch {
    stored = null;
  }
  const wipe = shouldWipe(stored, targetVersion);
  if (wipe) {
    resetNexusDexLocal();
  }
  try {
    localStorage.setItem(LAST_WIPED_VERSION_KEY, targetVersion);
  } catch {
    /* quota — the wipe still ran; worst case it re-runs next launch */
  }

  // Schedule the authoritative server wipe.
  //
  // 2026-08-30 audit: the AUDIT-BACKFILL arm was retired along with the server
  // DELETE it existed to schedule. Since runDeferredRemoteWipe stopped issuing
  // the DELETE (2026-07-25), scheduling a backfill only set a flag the defuser
  // immediately cleared - while ALSO arming the bulk tombstone below, which
  // briefly suppressed every server pull until the defuser's microtask won the
  // race. Pure churn plus a data-loss race window, for an obligation that no
  // longer exists. Only a genuine local wipe (an actually-old marker) still
  // schedules; the audit version is stamped regardless so this decision is
  // recorded once.
  let remoteWipeScheduled = false;
  const storedAudit = readRemoteWipeAuditVersion();
  if (wipe) {
    // Fresh local wipe this launch: the server must be cleared to match.
    setRemoteWipePending("fresh-wipe");
    remoteWipeScheduled = true;
  }
  // PR V: write the bulk reconciliation tombstone when a wipe was scheduled.
  // The bulk tombstone makes pullDexStateFromServer drop any incoming server
  // row older than the wipe, so a wiped local store stays empty even after
  // REMOTE_WIPE_PENDING_KEY clears. A single bulk timestamp covers every wiped
  // dexId; it is per-device and not synced (see the dexTombstones docstring).
  if (remoteWipeScheduled) {
    recordBulkTombstone();
  }
  // Stamp the audit version regardless so the backfill never re-triggers, even
  // on a fresh wipe (which already scheduled via the fresh-wipe path).
  if (storedAudit < REMOTE_WIPE_AUDIT_VERSION) {
    writeRemoteWipeAuditVersion(REMOTE_WIPE_AUDIT_VERSION);
  }

  return { wiped: wipe, markerWritten: targetVersion, remoteWipeScheduled };
}

// ── Deferred remote wipe — RETIRED (2026-07-25) ──────────────────────────────

export interface DeferredRemoteWipeResult {
  /** "retired" = the wipe is permanently disabled; any pending marker was
   *  defused. "deferred" = nothing to do (no marker / not authed). */
  outcome: "retired" | "deferred";
  /** Always 0 — no DELETE is ever attempted. */
  attempts: number;
}

/**
 * RETIRED. The clean-slate server wipe is permanently disabled.
 *
 * It was a PRE-LAUNCH cleanup that assumed the server `dex_records` table was
 * empty (migration 0029 wiped it before launch), so its `DELETE /api/dex/state`
 * was documented as "a no-op safety net". Post-launch that assumption is FALSE
 * — the server now holds real, synced user dexes — so firing the DELETE
 * destroyed live data. The audit-backfill path was the worst case: it re-fired
 * the DELETE on devices whose LOCAL wipe had long since run, deleting the server
 * dex shared by EVERY one of that account's logins (a direct login and a Google
 * login resolve to the same account) while the device silently kept its full
 * local dex. A user then saw a complete dex on-device but an empty one on the
 * server / admin.
 *
 * This no longer issues the DELETE under any condition. It only DEFUSES: it
 * clears any `REMOTE_WIPE_PENDING_KEY` a prior build left queued, so no device
 * can ever fire the wipe again. `resetNexusDexLocal` (local-only) is untouched;
 * restoring a server dex from the surviving local copy is handled by the
 * one-time resync-up (see dexResyncUp.ts).
 *
 * LIFETIME RULE (2026-08-30 audit): retired does NOT mean deletable. This
 * defuser is the only thing that clears a bulk tombstone armed by
 * runDexWipeMigration's genuine-wipe path, so it must live exactly as long as
 * any code path can arm one. Retire the two together, never separately -
 * deleting this "dead" function alone would leave a wiped device dropping
 * every server row on every pull, forever.
 */
export async function runDeferredRemoteWipe(
  _apiFetch: (method: string, url: string, data?: unknown) => Promise<Response>,
  _isAuthenticated: () => boolean,
): Promise<DeferredRemoteWipeResult> {
  const hadMarker = isRemoteWipePending();
  // Defuse any queued wipe so it can never fire on this or a future launch.
  clearRemoteWipePending();
  // Also clear the bulk wipe tombstone: with the wipe retired it would otherwise
  // keep dropping legitimate server rows older than the wipe on every pull,
  // suppressing a dex restored from the surviving local copy (dexResyncUp.ts).
  const clearedTombstone = clearBulkTombstone();
  return { outcome: hadMarker || clearedTombstone ? "retired" : "deferred", attempts: 0 };
}

/**
 * Post-launch defuse (formerly the deferred wipe scheduler). Kept and still
 * called from App so any device carrying a stale pending-wipe marker clears it
 * on next launch. It never contacts the server. queueMicrotask keeps it off the
 * paint-critical path.
 */
export function scheduleDeferredRemoteWipe(
  apiFetch: (method: string, url: string, data?: unknown) => Promise<Response>,
  isAuthenticated: () => boolean,
): void {
  const fire = () => {
    void runDeferredRemoteWipe(apiFetch, isAuthenticated).catch(() => {
      /* defuse is best-effort; a stuck marker is harmless now that the task
         never issues a DELETE */
    });
  };
  if (typeof queueMicrotask === "function") {
    queueMicrotask(fire);
  } else {
    void Promise.resolve().then(fire);
  }
}
